import styled from '@emotion/styled';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography
} from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { updateSelectedVersions } from '@/redux/slices/titleNavigationDrawerSlice';
import { selectSeasonEpisodes, selectTitleNavigationDrawerSlice } from '@/redux/selectors/titleNavigationDrawerSelectors';
import { LoadingStatuses } from '@/constants/constants';
import PolarisIcon from '@/components/_common/PolarisIcon';

const SeasonEpisodesList = () => {
  const dispatch = useAppDispatch();
  const { selectedTitlesMap, selectedVersions, statusById } = useAppSelector(selectTitleNavigationDrawerSlice);
  const seasonEpisodes = useAppSelector(selectSeasonEpisodes);

  const selectVersion = (selectedVersion: TitleEntityV2) => {
    dispatch(updateSelectedVersions(selectedVersion));
  };

  const isPending = (episodeId: TitleEntityV2['id']) => statusById?.[episodeId] === LoadingStatuses.pending;
  const isError = (episodeId: TitleEntityV2['id']) => statusById?.[episodeId] === LoadingStatuses.rejected;

  return (
    <SeasonEpisodesListWrapper>
      {seasonEpisodes.map((episode) => (
        <StyledAccordion key={episode.id}>
          <StyledAccordionSummary expandIcon={<PolarisIcon icon="down_filled" />}>
            <Typography color="textSecondary">E{Math.trunc(episode.sequence_order)}</Typography>
            <Typography>{episode.name}</Typography>
          </StyledAccordionSummary>
          <StyledAccordionDetails>
            {isPending(episode.id) && (
              <Grid container direction="column" rowGap={1}>
                {Array.from({ length: 2 }).map((_, index) => (
                  <Skeleton key={`versions-list-${episode.id}-skeleton-${index}`} variant="rounded" height={45} />
                ))}
              </Grid>
            )}
            {isError(episode.id) && (
              <Typography color="textSecondary" textAlign="center">
                No versions found
              </Typography>
            )}
            {!isPending(episode.id) && !isError(episode.id) && selectedTitlesMap?.[episode.id] && (
              <VersionsList disablePadding>
                {selectedTitlesMap[episode.id].children.map((version) => (
                  <VersionListItem disablePadding key={`episode-list-${episode.id}-version-${version.id}`}>
                    <VersionItemButton onClick={() => selectVersion(version)}>
                      <ListItemText primary={version.name} />
                      <ListItemIcon>
                        <Checkbox edge="end" checked={!!selectedVersions.find((sv) => sv.id === version.id)} disableRipple />
                      </ListItemIcon>
                    </VersionItemButton>
                  </VersionListItem>
                ))}
              </VersionsList>
            )}
          </StyledAccordionDetails>
        </StyledAccordion>
      ))}
    </SeasonEpisodesListWrapper>
  );
};

export default SeasonEpisodesList;

const SeasonEpisodesListWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: theme.spacing(1),
  maxHeight: 'calc(100dvh - 525px)',
  overflowY: 'auto',
  overscrollBehavior: 'contain'
}));

const StyledAccordion = styled(Accordion)(() => ({
  '&.MuiPaper-root.MuiAccordion-root': {
    borderRadius: 6
  },
  '&.MuiAccordion-root.Mui-expanded': {
    margin: 0
  }
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  '&.MuiAccordionSummary-root': {
    flexDirection: 'row',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: 6,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    },
    '&.Mui-expanded': {
      minHeight: 'auto',
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      '& .MuiAccordionSummary-content': {
        marginTop: 0,
        marginBottom: 0,
        '&.Mui-expanded .MuiTypography-root': {
          color: theme.palette.secondary.main,
          fontWeight: 'bold'
        }
      }
    }
  },
  '& .MuiAccordionSummary-content': {
    columnGap: theme.spacing(2)
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    '& svg': {
      height: 8,
      transition: 'all 0.3s ease-in-out'
    },
    '&.Mui-expanded': {
      transform: 'none',
      backgroundColor: theme.palette.background.paper,
      '& svg': {
        transform: 'rotate(180deg)',
        color: theme.palette.secondary.main
      }
    }
  }
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  borderBottomLeftRadius: 6,
  borderBottomRightRadius: 6,
  '&.MuiAccordionDetails-root': {
    padding: theme.spacing(1)
  }
}));

const VersionsList = styled(List)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: theme.spacing(0.5)
}));

const VersionListItem = styled(ListItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 6
}));

const VersionItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(0.5, 2),
  justifyContent: 'space-between',
  '& .MuiListItemIcon-root': {
    justifyContent: 'end'
  }
}));
